import { ApiResponse, MagicLinkResponse, ValidateMagicLinkResponse, ValidateSessionResponse } from '../types/partner'

class ApiService {
    private baseURL: string
    private timeout: number

    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000'
        this.timeout = 30000 // 30 seconds
    }

    private getAuthHeaders(): Record<string, string> {
        const token = localStorage.getItem('partnerToken')
        return token ? { 'Authorization': `Bearer ${token}` } : {}
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`

        const requestHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders(),
            ...(options.headers as Record<string, string> || {})
        }

        const requestConfig: RequestInit = {
            ...options,
            headers: requestHeaders
        }

        try {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), this.timeout)

            const response = await fetch(url, {
                ...requestConfig,
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`)
            }

            return data
        } catch (error) {
            console.error(`API Error [${options.method || 'GET'} ${endpoint}]:`, error)

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error('Request timeout')
                }
                throw error
            }

            throw new Error('Unknown error occurred')
        }
    }

    // Public endpoints

    async requestAccess(email: string): Promise<ApiResponse<MagicLinkResponse>> {
        return this.makeRequest('/partners/request-access', {
            method: 'POST',
            body: JSON.stringify({ email })
        })
    }

    async validateMagicLink(token: string): Promise<ApiResponse<ValidateMagicLinkResponse>> {
        return this.makeRequest(`/partners/validate-magic-link?token=${encodeURIComponent(token)}`)
    }

    // Protected endpoints

    async validateSession(): Promise<ApiResponse<ValidateSessionResponse>> {
        return this.makeRequest('/partners/validate-session')
    }

    async logout(): Promise<ApiResponse<any>> {
        return this.makeRequest('/partners/logout', {
            method: 'POST'
        })
    }

    async getPartnerInfo(): Promise<ApiResponse<any>> {
        return this.makeRequest('/partners/me')
    }

    // Generic methods

    async get<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.makeRequest(endpoint, { method: 'GET' })
    }

    async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
        return this.makeRequest(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        })
    }
}

// Export singleton instance
export const apiService = new ApiService()
export default apiService
